import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik';
import * as Yup from 'yup';
import { InputText } from 'primereact/inputtext';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import axios from 'axios';
import { getSession } from 'next-auth/react';
import { Toast } from 'primereact/toast';
import { closePopup } from '@solidstarters/solid-core-ui/dist/redux/features/popupSlice';
import { useDispatch } from 'react-redux';
import { Dialog } from 'primereact/dialog';


const API_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL; 

interface HoldFormValues {
  remarks: string;
  holdContainer: string;
}

const validationSchema = Yup.object({
  remarks: Yup.string().required('Remarks is required'),
  holdContainer: Yup.string().required('Hold Container is required'),
});

const HoldContainer = (event: any) => {
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);
  const formikRef = useRef<FormikProps<HoldFormValues>>(null);

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState<HoldFormValues | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false); 

  const options = ['DRI Hold', 'Customs Hold', 'Auctioned Containers', 'Off-Hired Containers', 'SOC Containers'];


  const searchSuggestions = (event: any) => {
    const query = event.query.toLowerCase();
    const filtered = options.filter((option) => option.toLowerCase().includes(query));
    setSuggestions(filtered);
  };

  const handleSubmit = (values: HoldFormValues) => {
    setPendingValues(values);
    setIsConfirmDialogVisible(true);
  };

  const proceedWithSave = async () => {
    if (!pendingValues) return;
    const id = event?.rowData?.id;

    try {
      const session: any = await getSession();
      const token = session?.user?.accessToken || '';

      await axios.patch(`${API_URL}/api/bond-transaction-details/${id}`, pendingValues, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Data updated successfully.',
        life: 3000,
      });

      setIsConfirmDialogVisible(false);
      setPendingValues(null);

      setTimeout(() => {
        dispatch(closePopup());
      }, 500);
      window.location.reload();


    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
    }
  };


  const fetchExistingData = async (isAdmin: boolean) => {
    try {
      const id = event?.rowData?.id;
      if (!id) return;

      const session: any = await fetchUser();
      const token = session?.user?.accessToken || '';

      const response = await axios.get(`${API_URL}/api/bond-transaction-details/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(response,'response');
      

      const record = response?.data?.data;

      if (record && formikRef.current) {
        formikRef.current.setValues({
          remarks: record.remarks || '',
          holdContainer: record.holdContainer || '',
        });

        if (record.holdContainer === 'SOC Containers' && !isAdmin) {
          setIsReadOnly(true);
        } else {
          setIsReadOnly(false);
        }
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load existing data.',
        life: 3000,
      });
    }
  };

  useEffect(() => {
    const loadSession = async () => {
      const session = await fetchUser();
      const roles = session?.user?.user?.roles || [];
      const adminCheck = roles?.includes("Admin");
      fetchExistingData(adminCheck); 
    };
    loadSession();
  }, []);

  return (
    <div className=" mt-10 px-4 bg-white rounded-xl shadow-lg border border-gray-200">
      <Toast ref={toast} />

      <Dialog
        visible={isConfirmDialogVisible}
        header="Confirm Update"
        modal
        onHide={() => setIsConfirmDialogVisible(false)}
        footer={() => (
          <div className="flex justify-content-start gap-2">
            <Button
              label="Yes, Proceed"
              icon="pi pi-check"
              className="p-button-danger"
              onClick={proceedWithSave}
            />
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={() => {
                setIsConfirmDialogVisible(false);
                dispatch(closePopup());
              }}
            />
          </div>
        )}
      >
        <p>Are you sure you want to update this container record?</p>
      </Dialog>

      {/* 🔹 Formik Form */}
      <Formik
        innerRef={formikRef}
        initialValues={{
          remarks: '',
          holdContainer: '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values, isSubmitting }) => (
          <Form className="space-y-6 py-4 px-2">
            {/* Remarks Field */}
            <div className="mb-3">
              <label htmlFor="remarks" className="block text-sm font-semibold text-gray-700 mb-2">
                Remarks
              </label>
              <Field
                name="remarks"
                as={InputText}
                className="w-full p-inputtext-sm"
                placeholder="Enter remarks"
                disabled={isReadOnly} 
              />
              <ErrorMessage
                name="remarks"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            {/* Hold Container Field */}
            <div className="mb-3">
              <label htmlFor="holdContainer" className="block text-sm font-semibold text-gray-700 mb-2">
                Hold Container
              </label>
              <AutoComplete
                id="holdContainer"
                suggestions={suggestions}
                completeMethod={searchSuggestions}
                value={values.holdContainer}
                onChange={(e) => setFieldValue('holdContainer', e.value)}
                placeholder="Search or select..."
                className="w-full p-inputtext-sm"
                dropdown
                disabled={isReadOnly}
              />
              <ErrorMessage
                name="holdContainer"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end align-items-center gap-4 mt-4">
              <Button
                label="Cancel"
                className="p-button-secondary"
                type="button"
                onClick={() => dispatch(closePopup())}
              />

              {isReadOnly ? (
                <p className="text-red-500 font-medium">
                  Please contact admin to update these container
                </p>
              ) : (
                <Button
                  label="Save"
                  icon="pi pi-check"
                  type="submit"
                  loading={isSubmitting}
                />
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
export default HoldContainer;

const fetchUser = async () => {
  const session: any = await getSession();
  return session;
};
